import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Upload,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Select,
  Modal,
  Form,
  message,
  Alert,
  Progress,
  Input,
  Statistic,
  Checkbox,
  Tooltip,
  Divider
} from 'antd';
import {
  InboxOutlined,
  UploadOutlined,
  TagOutlined,
  DeleteOutlined,
  EyeOutlined,
  CloudUploadOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  CheckSquareOutlined,
  BorderOutlined,
  SelectOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useStore } from '../../stores/useStore';
import { apiService } from '../../services/api';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;
const { Search } = Input;

interface MediaFile {
  file_id: string;
  filename: string;
  original_name: string;
  file_size: number;
  file_type: string;
  gcs_path: string;
  upload_date: string;
  media_type?: 'script' | 'transcript';
  content_category?: string;
  processing_status: 'uploaded' | 'processing' | 'processed' | 'error';
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed';
  file_hash?: string;
  mime_type?: string;
  ai_processing_status?: string;
  processing_version?: string;
  last_processed_at?: string;
  ai_provider?: string;
  ai_model?: string;
  ai_processing_jobs?: Array<{
    job_id: string;
    ai_provider: string;
    model_name: string;
    status: string;
    processing_type: string;
    created_at?: string;
    started_at?: string;
    completed_at?: string;
  }>;
}

// Content categories for tagging
const CONTENT_CATEGORIES = [
  { label: 'Movies', value: 'movie' },
  { label: 'TV Shows', value: 'tv_show' },
  { label: 'Podcasts', value: 'podcast' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'Books', value: 'book' },
  { label: 'Articles', value: 'article' },
  { label: 'Other', value: 'other' }
];

// AI Providers for processing - Latest models first
const AI_PROVIDERS = [
  // Vertex AI (Google Cloud) - recommended for multimodal batch processing
  { label: 'Vertex AI (Gemini 2.5 Flash) ★', value: 'vertex-ai-gemini-2.5-flash', recommended: true, provider: 'vertex-ai', model: 'gemini-2.5-flash' },
  { label: 'Vertex AI (Gemini 2.5 Pro)', value: 'vertex-ai-gemini-2.5-pro', provider: 'vertex-ai', model: 'gemini-2.5-pro' },

  // OpenAI - Latest first
  { label: 'OpenAI (GPT-4o)', value: 'openai-gpt-4o', provider: 'openai', model: 'gpt-4o' },
  { label: 'OpenAI (GPT-4o Mini)', value: 'openai-gpt-4o-mini', provider: 'openai', model: 'gpt-4o-mini' },
  { label: 'OpenAI (GPT-4 Turbo)', value: 'openai-gpt-4-turbo', provider: 'openai', model: 'gpt-4-turbo' },
  { label: 'OpenAI (GPT-4)', value: 'openai-gpt-4', provider: 'openai', model: 'gpt-4' },

  // Anthropic - Latest first
  { label: 'Anthropic (Claude 3.5 Sonnet)', value: 'anthropic-claude-3.5-sonnet', provider: 'anthropic', model: 'claude-3.5-sonnet' },
  { label: 'Anthropic (Claude 3 Opus)', value: 'anthropic-claude-3-opus', provider: 'anthropic', model: 'claude-3-opus' },
  { label: 'Anthropic (Claude 3 Sonnet)', value: 'anthropic-claude-3-sonnet', provider: 'anthropic', model: 'claude-3-sonnet' },
  { label: 'Anthropic (Claude 3 Haiku)', value: 'anthropic-claude-3-haiku', provider: 'anthropic', model: 'claude-3-haiku' },

  // Google AI Studio - Latest first
  { label: 'Google AI (Gemini 2.5 Flash)', value: 'google-gemini-2.5-flash', provider: 'google', model: 'gemini-2.5-flash' },
  { label: 'Google AI (Gemini 2.5 Pro)', value: 'google-gemini-2.5-pro', provider: 'google', model: 'gemini-2.5-pro' },
];

const MediaManager: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [studyName, setStudyName] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    media_type: undefined,
    content_category: undefined,
    processing_status: undefined
  });
  const [form] = Form.useForm();
  const [uploadForm] = Form.useForm();
  
  // Batch operations state
  const [batchMode, setBatchMode] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [batchProcessing, setBatchProcessing] = useState(false);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadMediaFiles = useCallback(async () => {
    setLoading(true);

    try {
      const files = await apiService.getMediaFiles(studyId!, {
        media_type: filters.media_type,
        content_category: filters.content_category,
        processing_status: filters.processing_status
      });

      // Transform API response to match MediaFile interface
      const transformedFiles: MediaFile[] = files.map(file => ({
        file_id: file.file_id,
        filename: file.filename,
        original_name: file.original_name,
        file_size: file.file_size,
        file_type: file.file_type,
        gcs_path: file.gcs_path,
        upload_date: file.upload_date,
        media_type: file.media_type,
        content_category: file.content_category,
        processing_status: file.processing_status,
        analysis_status: file.analysis_status || file.ai_processing_status || 'pending',
        file_hash: file.file_hash,
        mime_type: file.mime_type,
        ai_processing_status: file.ai_processing_status,
        processing_version: file.processing_version,
        last_processed_at: file.last_processed_at,
        ai_provider: file.ai_provider,
        ai_model: file.ai_model,
        ai_processing_jobs: file.ai_processing_jobs
      }));

      setMediaFiles(transformedFiles);
    } catch (error) {
      console.error('Failed to load media files:', error);
      message.error('Failed to load media files');
    } finally {
      setLoading(false);
    }
  }, [studyId, filters]);

  useEffect(() => {
    if (studyId) {
      loadMediaFiles();
      loadStudyName();
    }
  }, [studyId, filters, loadMediaFiles]);

  useEffect(() => {
    const hasProcessingFile = mediaFiles.some(f => f.processing_status === 'processing');

    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (hasProcessingFile) {
      pollingIntervalRef.current = setInterval(() => {
        loadMediaFiles();
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [mediaFiles, loadMediaFiles]);

  const loadStudyName = async () => {
    if (!studyId) return;

    try {
      const study = await apiService.getStudy(studyId);
      setStudyName(study.project_name || `Study ${studyId}`);
    } catch (error) {
      console.error('Failed to fetch study name:', error);
      setStudyName(`Study ${studyId}`);
    }
  };

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles(files);
    setUploadModalVisible(true);
  };

  const handleUploadWithTags = async (values: any) => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadModalVisible(false);

    try {
      // Upload each file with the selected tags
      for (const file of selectedFiles) {
        await apiService.uploadMediaFile(studyId!, file, {
          media_type: values.media_type,
          content_category: values.content_category,
          ai_providers: values.ai_providers || ['vertex-ai-gemini-2.5-flash']
        });
      }

      message.success(`${selectedFiles.length} file(s) uploaded and tagged successfully`);
      setSelectedFiles([]);
      uploadForm.resetFields();
      
      // Reload the media files to show the newly uploaded files
      await loadMediaFiles();
    } catch (error) {
      console.error('Failed to upload files:', error);
      message.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleTagFile = (file: MediaFile) => {
    setSelectedFile(file);
    
    // Convert existing AI provider and model to the new format
    let existingAiProviders: string[] = [];
    if (file.ai_provider && file.ai_model) {
      // Find matching provider option based on provider and model
      const matchingProvider = AI_PROVIDERS.find(p => 
        p.provider === file.ai_provider && p.model === file.ai_model
      );
      
      if (matchingProvider) {
        existingAiProviders = [matchingProvider.value];
      } else {
        // Fallback for legacy format
        existingAiProviders = [`${file.ai_provider}-${file.ai_model}`];
      }
    }
    
    form.setFieldsValue({
      media_type: file.media_type,
      content_category: file.content_category,
      ai_providers: existingAiProviders
    });
    setTagModalVisible(true);
  };

  const handleSaveTags = async (values: any) => {
    if (!selectedFile) return;

    try {
      await apiService.updateMediaFile(studyId!, selectedFile.file_id, {
        media_type: values.media_type,
        content_category: values.content_category,
        ai_providers: values.ai_providers
      });

      setTagModalVisible(false);
      setSelectedFile(null);
      message.success('File tagged successfully');
      
      // Reload the media files to show the updates
      await loadMediaFiles();
    } catch (error) {
      console.error('Failed to tag file:', error);
      message.error('Failed to tag file');
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await apiService.deleteMediaFile(studyId!, fileId);
      message.success('File deleted successfully');
      
      // Reload the media files to reflect the deletion
      await loadMediaFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      message.error('Failed to delete file');
    }
  };

  const handleProcessFile = async (record: MediaFile) => {
    try {
      // For now, default to Vertex AI - in future this could be configurable
      const aiProviders = ['vertex-ai-gemini-2.5-flash'];
      
      await apiService.reprocessMediaFile(studyId!, record.file_id, aiProviders);
      message.success('Processing started! This may take a few minutes.');
      
      // Reload the media files to show updated status
      await loadMediaFiles();
    } catch (error) {
      console.error('Failed to process file:', error);
      message.error('Failed to start processing');
    }
  };

  const handleReprocessFile = async (record: MediaFile) => {
    try {
      // For now, default to Vertex AI - in future this could be configurable
      const aiProviders = ['vertex-ai-gemini-2.5-flash'];
      
      await apiService.reprocessMediaFile(studyId!, record.file_id, aiProviders);
      message.success('Reprocessing started! This may take a few minutes.');
      
      // Reload the media files to show updated status
      await loadMediaFiles();
    } catch (error) {
      console.error('Failed to reprocess file:', error);
      message.error('Failed to start reprocessing');
    }
  };

  // Batch operations handlers
  const handleBatchModeToggle = () => {
    setBatchMode(!batchMode);
    setSelectedFileIds([]);
  };

  const handleSelectAll = () => {
    const allFileIds = mediaFiles.map(file => file.file_id);
    setSelectedFileIds(allFileIds);
  };

  const handleSelectPending = () => {
    const pendingFileIds = mediaFiles
      .filter(file => file.analysis_status === 'pending')
      .map(file => file.file_id);
    setSelectedFileIds(pendingFileIds);
  };

  const handleDeselectAll = () => {
    setSelectedFileIds([]);
  };

  const handleFileSelection = (fileId: string, checked: boolean) => {
    if (checked) {
      setSelectedFileIds(prev => [...prev, fileId]);
    } else {
      setSelectedFileIds(prev => prev.filter(id => id !== fileId));
    }
  };

  const handleBatchDelete = () => {
    if (selectedFileIds.length === 0) {
      message.warning('Please select files to delete');
      return;
    }

    Modal.confirm({
      title: 'Confirm Batch Delete',
      content: `Are you sure you want to delete ${selectedFileIds.length} selected file(s)? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setBatchProcessing(true);
        try {
          // Delete files one by one (could be optimized with batch API endpoint)
          const deletePromises = selectedFileIds.map(fileId => 
            apiService.deleteMediaFile(studyId!, fileId)
          );
          
          await Promise.all(deletePromises);
          
          message.success(`${selectedFileIds.length} file(s) deleted successfully`);
          setSelectedFileIds([]);
          await loadMediaFiles();
        } catch (error) {
          console.error('Failed to delete files:', error);
          message.error('Failed to delete some files');
        } finally {
          setBatchProcessing(false);
        }
      }
    });
  };

  const handleBatchProcess = () => {
    if (selectedFileIds.length === 0) {
      message.warning('Please select files to process');
      return;
    }

    Modal.confirm({
      title: 'Confirm Batch Processing',
      content: `Are you sure you want to process ${selectedFileIds.length} selected file(s) with Vertex AI (Gemini 1.5 Pro)? This may take several minutes.`,
      okText: 'Process',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setBatchProcessing(true);
        try {
          // Process files one by one (could be optimized with batch API endpoint)
          const processPromises = selectedFileIds.map(fileId => 
            apiService.reprocessMediaFile(studyId!, fileId, ['vertex-ai-gemini-2.5-flash'])
          );
          
          await Promise.all(processPromises);
          
          message.success(`${selectedFileIds.length} file(s) processing started successfully`);
          setSelectedFileIds([]);
          await loadMediaFiles();
        } catch (error) {
          console.error('Failed to process files:', error);
          message.error('Failed to process some files');
        } finally {
          setBatchProcessing(false);
        }
      }
    });
  };

  const getProcessingProgress = (
    jobs: MediaFile['ai_processing_jobs']
  ): { completed: number; total: number } | null => {
    if (!jobs || jobs.length === 0) return null;
    const questionJobs = jobs.filter(j => j.processing_type === 'research_question');
    if (questionJobs.length === 0) return null;
    const completed = questionJobs.filter(j => j.status === 'completed').length;
    return { completed, total: questionJobs.length };
  };

  const columns = [
    {
      title: 'File',
      dataIndex: 'original_name',
      key: 'original_name',
      width: 250,
      render: (name: string, record: MediaFile) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {(record.file_size / 1024 / 1024).toFixed(2)} MB
          </Text>
        </div>
      ),
    },
    {
      title: 'Media Type',
      dataIndex: 'media_type',
      key: 'media_type',
      width: 120,
      render: (type: string) => (
        type ? (
          <Tag color={type === 'script' ? 'blue' : 'green'}>
            {type.toUpperCase()}
          </Tag>
        ) : (
          <Tag color="default">Not Tagged</Tag>
        )
      ),
    },
    {
      title: 'Content Category',
      dataIndex: 'content_category',
      key: 'content_category',
      width: 150,
      render: (category: string) => (
        category ? (
          <Tag color="purple">{category.replace('_', ' ').toUpperCase()}</Tag>
        ) : (
          <Tag color="default">Not Tagged</Tag>
        )
      ),
    },
    {
      title: 'Processing Status',
      dataIndex: 'processing_status',
      key: 'processing_status',
      width: 180,
      render: (status: string, record: MediaFile) => {
        if (status === 'processing') {
          const progress = getProcessingProgress(record.ai_processing_jobs);
          if (progress) {
            const percent = progress.total > 0
              ? Math.round((progress.completed / progress.total) * 100)
              : 0;
            return (
              <div style={{ minWidth: 160 }}>
                <Text style={{ fontSize: '12px', color: '#1890ff', display: 'block', marginBottom: 4 }}>
                  {progress.completed} / {progress.total} questions
                </Text>
                <Progress percent={percent} size="small" status="active" showInfo={false} strokeColor="#1890ff" />
              </div>
            );
          }
          return <Tag color="blue">PROCESSING</Tag>;
        }
        const colors: Record<string, string> = { uploaded: 'orange', processed: 'green', error: 'red' };
        return <Tag color={colors[status] ?? 'default'}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Analysis Status',
      dataIndex: 'analysis_status',
      key: 'analysis_status',
      width: 120,
      render: (status: string) => {
        const colors = {
          pending: 'default',
          analyzing: 'processing',
          completed: 'success',
          failed: 'error'
        };
        return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'AI Provider',
      dataIndex: 'ai_provider',
      key: 'ai_provider',
      width: 200,
      render: (provider: string, record: MediaFile) => {
        if (!provider || !record.ai_model) {
          return <Tag color="default">Not Selected</Tag>;
        }
        
        // Format the display name
        const formatProviderName = (provider: string, model: string) => {
          const providerMap: { [key: string]: string } = {
            'vertex-ai': 'Vertex AI',
            'openai': 'OpenAI',
            'anthropic': 'Anthropic',
            'google': 'Google'
          };
          
          const modelMap: { [key: string]: string } = {
            'gemini-2.5-flash': 'Gemini 2.5 Flash',
            'gemini-2.5-pro': 'Gemini 2.5 Pro',
            'gemini-1.5-pro': 'Gemini 1.5 Pro',
            'gemini-1.5-flash': 'Gemini 1.5 Flash',
            'gpt-4': 'GPT-4',
            'gpt-4-turbo': 'GPT-4 Turbo',
            'claude-3-sonnet': 'Claude 3 Sonnet',
            'claude-3-opus': 'Claude 3 Opus'
          };
          
          const providerName = providerMap[provider] || provider;
          const modelName = modelMap[model] || model;
          
          return `${providerName} (${modelName})`;
        };
        
        const displayName = formatProviderName(provider, record.ai_model);
        
        // Color based on provider
        const getProviderColor = (provider: string) => {
          switch (provider) {
            case 'vertex-ai': return 'blue';
            case 'openai': return 'green';
            case 'anthropic': return 'orange';
            case 'google': return 'purple';
            default: return 'default';
          }
        };
        
        return (
          <Tag color={getProviderColor(provider)} style={{ maxWidth: '180px' }}>
            {displayName}
          </Tag>
        );
      },
    },
    {
      title: 'Upload Date',
      dataIndex: 'upload_date',
      key: 'upload_date',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: batchMode ? 150 : 200,
      render: (_: any, record: MediaFile) => {
        const isProcessed = record.processing_status === 'processed';
        const isProcessing = record.processing_status === 'processing';
        
        return (
          <Space size="small">
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              onClick={async () => {
                try {
                  const previewData = await apiService.getMediaFilePreview(studyId!, record.file_id);
                  window.open(previewData.signed_url, '_blank');
                } catch (error) {
                  console.error('Failed to get file preview:', error);
                  message.error('Failed to preview file');
                }
              }}
            />
            {!batchMode && (
              <>
                <Button 
                  size="small" 
                  icon={<TagOutlined />}
                  onClick={() => handleTagFile(record)}
                />
                {!isProcessed && !isProcessing && (
                  <Button 
                    size="small" 
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleProcessFile(record)}
                    title="Process with Vertex AI"
                  >
                    Process
                  </Button>
                )}
                {isProcessed && (
                  <Button 
                    size="small" 
                    icon={<RedoOutlined />}
                    onClick={() => handleReprocessFile(record)}
                    title="Reprocess with Vertex AI"
                  >
                    Reprocess
                  </Button>
                )}
                <Button 
                  size="small" 
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteFile(record.file_id)}
                />
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const summary = {
    total: mediaFiles.length,
    tagged: mediaFiles.filter(f => f.media_type && f.content_category).length,
    processed: mediaFiles.filter(f => f.processing_status === 'processed').length,
    analyzed: mediaFiles.filter(f => f.analysis_status === 'completed').length
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16 }}>
          Media Manager
        </Title>
        <Paragraph>
          Upload, tag, and manage media files for {studyName || `Study ${studyId}`}
        </Paragraph>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Files"
              value={summary.total}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Tagged Files"
              value={summary.tagged}
              suffix={`/ ${summary.total}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Processed"
              value={summary.processed}
              suffix={`/ ${summary.total}`}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Analyzed"
              value={summary.analyzed}
              suffix={`/ ${summary.total}`}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Upload Section */}
      <Card title="Upload Media Files" style={{ marginBottom: 16 }}>
        <Alert
          message="Supported File Types"
          description="Upload .txt and .pdf files for scripts and transcripts"
          type="info"
          style={{ marginBottom: 16 }}
        />

        <Dragger 
          accept=".txt,.pdf"
          beforeUpload={(file, fileList) => {
            handleFileSelect(fileList);
            return false; // Prevent default upload
          }}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag .txt and .pdf files to this area to upload
          </p>
          <p className="ant-upload-hint">
            You'll be prompted to tag files before upload begins
          </p>
        </Dragger>

        {uploading && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Progress percent={75} status="active" />
            <Text>Uploading to Google Cloud Storage...</Text>
          </div>
        )}
      </Card>

      {/* Filters and Batch Operations */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filter by media type"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, media_type: value }))}
            >
              <Select.Option value="script">Script</Select.Option>
              <Select.Option value="transcript">Transcript</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filter by content category"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, content_category: value }))}
            >
              {CONTENT_CATEGORIES.map((category) => (
                <Select.Option key={category.value} value={category.value}>
                  {category.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, processing_status: value }))}
            >
              <Select.Option value="uploaded">Uploaded</Select.Option>
              <Select.Option value="processing">Processing</Select.Option>
              <Select.Option value="processed">Processed</Select.Option>
              <Select.Option value="error">Error</Select.Option>
            </Select>
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <Search
              placeholder="Search files..."
              onSearch={(value) => console.log('Search:', value)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
        
        <Divider />
        
        {/* Batch Operations Controls */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                type={batchMode ? 'primary' : 'default'}
                icon={batchMode ? <CheckSquareOutlined /> : <BorderOutlined />}
                onClick={handleBatchModeToggle}
              >
                {batchMode ? 'Exit Batch Mode' : 'Batch Operations'}
              </Button>
              
              {batchMode && (
                <>
                  <Button
                    icon={<SelectOutlined />}
                    onClick={handleSelectAll}
                    disabled={mediaFiles.length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    icon={<ClockCircleOutlined />}
                    onClick={handleSelectPending}
                    disabled={mediaFiles.filter(f => f.analysis_status === 'pending').length === 0}
                  >
                    Select Pending
                  </Button>
                  <Button
                    onClick={handleDeselectAll}
                    disabled={selectedFileIds.length === 0}
                  >
                    Deselect All
                  </Button>
                </>
              )}
            </Space>
          </Col>
          
          {batchMode && selectedFileIds.length > 0 && (
            <Col>
              <Space>
                <Text type="secondary">
                  {selectedFileIds.length} file(s) selected
                </Text>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleBatchProcess}
                  loading={batchProcessing}
                  disabled={selectedFileIds.length === 0}
                >
                  Process Selected
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBatchDelete}
                  loading={batchProcessing}
                  disabled={selectedFileIds.length === 0}
                >
                  Delete Selected
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {/* Files Table */}
      <Card title="Media Files">
        <Table
          columns={columns}
          dataSource={mediaFiles}
          rowKey="file_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} files`,
          }}
          rowSelection={batchMode ? {
            selectedRowKeys: selectedFileIds,
            onChange: (selectedRowKeys) => {
              setSelectedFileIds(selectedRowKeys as string[]);
            },
            onSelectAll: (selected) => {
              if (selected) {
                handleSelectAll();
              } else {
                handleDeselectAll();
              }
            },
            onSelect: (record, selected) => {
              handleFileSelection(record.file_id, selected);
            },
          } : undefined}
        />
      </Card>

      {/* Upload Modal */}
      <Modal
        title="Tag Files Before Upload"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedFiles([]);
          uploadForm.resetFields();
        }}
        onOk={() => uploadForm.submit()}
        width={500}
        okText="Upload & Tag"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Selected Files ({selectedFiles.length}):</Text>
          <div style={{ marginTop: 8 }}>
            {selectedFiles.map((file, index) => (
              <div key={index} style={{ fontSize: '12px', color: '#666' }}>
                • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            ))}
          </div>
        </div>

        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleUploadWithTags}
        >
          <Form.Item
            name="media_type"
            label="Media Type"
            rules={[{ required: true, message: 'Please select media type' }]}
          >
            <Select placeholder="Select media type">
              <Select.Option value="script">Script</Select.Option>
              <Select.Option value="transcript">Transcript</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="content_category"
            label="Content Category"
            rules={[{ required: true, message: 'Please select content category' }]}
          >
            <Select placeholder="Select content category">
              {CONTENT_CATEGORIES.map((category) => (
                <Select.Option key={category.value} value={category.value}>
                  {category.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ai_providers"
            label="AI Providers"
            tooltip="Select which AI providers will analyze this media file. Vertex AI is recommended for context caching capabilities."
            initialValue={['vertex-ai-gemini-2.5-flash']}
          >
            <Select
              mode="multiple"
              placeholder="Select AI providers"
              allowClear
            >
              {AI_PROVIDERS.map((provider) => (
                <Select.Option key={provider.value} value={provider.value}>
                  {provider.label}
                  {provider.recommended && <Text style={{ color: '#52c41a', fontSize: '12px' }}> (Recommended)</Text>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Tag Modal */}
      <Modal
        title="Tag Media File"
        open={tagModalVisible}
        onCancel={() => setTagModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTags}
        >
          {selectedFile && (
            <div>
              <Paragraph>
                <Text strong>File:</Text> {selectedFile.original_name}
              </Paragraph>
              
              <Form.Item
                name="media_type"
                label="Media Type"
                rules={[{ required: true, message: 'Please select media type' }]}
              >
                <Select placeholder="Select media type">
                  <Select.Option value="script">Script</Select.Option>
                  <Select.Option value="transcript">Transcript</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="content_category"
                label="Content Category"
                rules={[{ required: true, message: 'Please select content category' }]}
              >
                <Select placeholder="Select content category">
                  {CONTENT_CATEGORIES.map((category) => (
                    <Select.Option key={category.value} value={category.value}>
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="ai_providers"
                label="AI Providers"
                tooltip="Select which AI providers will analyze this media file"
              >
                <Select
                  mode="multiple"
                  placeholder="Select AI providers"
                  allowClear
                >
                  {AI_PROVIDERS.map((provider) => (
                    <Select.Option key={provider.value} value={provider.value}>
                      {provider.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default MediaManager; 